import { useEffect } from 'react';
import { useSimStore } from '../store/useSimStore';
import { buildPayload, applyPayload } from './syncPayload';

/**
 * Two-device sync via a WebSocket relay (server/wsServer.js) on the same LAN.
 * Instructor publishes; monitor/vent/CTG screens receive. Payload shape and
 * apply-logic are shared with the BroadcastChannel transport (syncPayload.js).
 *
 * ── When it activates (resolved at RUNTIME — no rebuild needed) ─────────────
 *   • ?ws=1            → relay on this page's host, port 8080
 *   • ?ws=<host[:p]>   → explicit relay address (also accepts ws://…)
 *   • ?ws=0            → force OFF
 *   • plain http:// page on a NON-localhost host (i.e. the app is being served
 *     over the LAN — `npm run lan`) → auto-connect to ws://<that host>:8080
 *   • VITE_SYNC_MODE=websocket (+ optional VITE_WS_HOST) → legacy env opt-in
 *   Otherwise (the HTTPS production site, or plain localhost dev) it does
 *   nothing: browsers block insecure ws:// from https:// pages, so the
 *   cloud-hosted site cannot reach a LAN relay — serve the app from the
 *   relay machine instead (npm run lan).
 *
 * Same design rule as useBroadcastSync: never put the reactive store in deps —
 * publisher uses useSimStore.subscribe, receiver applies via getState().
 */
function resolveWsUrl() {
  if (typeof window === 'undefined') return null;
  const param = new URLSearchParams(window.location.search).get('ws');
  if (param === '0') return null;
  if (param && param !== '1') {
    return param.includes('://') ? param : `ws://${param.includes(':') ? param : `${param}:8080`}`;
  }
  if (param === '1') return `ws://${window.location.hostname}:8080`;
  if (import.meta.env.VITE_SYNC_MODE === 'websocket') {
    return import.meta.env.VITE_WS_HOST || `ws://${window.location.hostname}:8080`;
  }
  // Auto: page served over plain http from a LAN host → same host runs the relay.
  const lanServed = window.location.protocol === 'http:'
    && !['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
  return lanServed ? `ws://${window.location.hostname}:8080` : null;
}

export function useWebSocketSync(isPublisher = false) {
  useEffect(() => {
    const url = resolveWsUrl();
    if (!url) return;   // WS mode not active on this page — broadcast-only

    let ws = null;
    let closed = false;
    let reconnectTimer = null;
    let attempts = 0;
    let unsubscribe = null;

    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        attempts = 0;
        useSimStore.getState().setIsConnected(true);
        if (isPublisher) {
          // Same rationale as the broadcast publisher: push the current snapshot
          // immediately so a monitor that connected first (or a refreshed
          // instructor) is brought up to date without waiting for a change.
          ws.send(JSON.stringify({ type: 'STATE_UPDATE', payload: buildPayload(useSimStore.getState()) }));
        }
      };

      ws.onmessage = async (event) => {
        // The relay echoes to every client including the sender — the publisher
        // ignores incoming state (it is the source of truth).
        if (isPublisher) return;
        try {
          // Tolerate binary frames (a relay that doesn't preserve the text
          // frame type delivers Blobs) as well as plain text.
          const text = typeof event.data === 'string' ? event.data : await event.data.text();
          const { type, payload } = JSON.parse(text);
          if (type === 'STATE_UPDATE') applyPayload(payload);
        } catch { /* malformed frame — ignore */ }
      };

      ws.onclose = () => {
        useSimStore.getState().setIsConnected(false);
        scheduleReconnect();
      };
      ws.onerror = () => { /* onclose follows and handles reconnect */ };
    };

    // Reconnect forever with capped backoff — a sim session may start the
    // relay/instructor in any order, and wifi can blip mid-case.
    const scheduleReconnect = () => {
      if (closed) return;
      const backoffMs = Math.min(1000 * Math.pow(1.5, attempts), 15000);
      attempts += 1;
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, backoffMs);
    };

    if (isPublisher) {
      unsubscribe = useSimStore.subscribe((state) => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'STATE_UPDATE', payload: buildPayload(state) }));
        }
      });
    }

    connect();

    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      if (unsubscribe) unsubscribe();
      if (ws) { ws.onclose = null; ws.close(); }
    };
  }, [isPublisher]);
}
